"use client";
import { useCallback, useEffect, useState } from "react";

import { orderApi } from "@/api/services";
import { useAuth } from "@/store/auth";
import type { OrderOutput } from "@/api/online-ordering-service/model";

type UseOrdersResult = {
  orders: OrderOutput[];
  total: number;
  loading: boolean;
  hasMore: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
};

/**
 * Fetches the current customer's order history.
 *
 * - When the user is not logged in, returns an empty list (no error, no fetch).
 * - On login state change, refetches automatically.
 */
export function useOrders(): UseOrdersResult {
  const { isLoggedIn } = useAuth();
  const [orders, setOrders] = useState<OrderOutput[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setOrders([]);
      setTotal(0);
      setNextCursor(null);
      setHasMore(false);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await orderApi.listMine();
      setOrders(res.data);
      setTotal(res.total);
      setNextCursor(res.nextCursor || null);
      setHasMore(res.hasMore || false);
    } catch (err) {
      console.warn("[useOrders] listMine failed", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setOrders([]);
      setTotal(0);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !nextCursor) return;
    setLoading(true);
    setError(null);
    try {
      const res = await orderApi.listMine({ cursor: nextCursor });
      setOrders((prev) => [...prev, ...res.data]);
      setTotal(res.total); // Usually total remains same but good to update
      setNextCursor(res.nextCursor || null);
      setHasMore(res.hasMore || false);
    } catch (err) {
      console.warn("[useOrders] loadMore failed", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, nextCursor]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { orders, total, loading, hasMore, error, refresh, loadMore };
}
